<template>
  <div
    role="radiogroup"
    data-segmento
    :aria-label="ariaLabel"
    :aria-disabled="disabled ? 'true' : undefined"
    :class="[
      'relative inline-grid auto-cols-fr grid-flow-col items-stretch rounded-lg border border-border-strong bg-glass-strong p-0.5',
      fullWidth && 'w-full',
    ]"
  >
    <!-- Evidenziazione che scorre da un'opzione all'altra: segmenti di pari larghezza, quindi solo CSS, nessuna misura -->
    <span
      v-if="indiceSelezionato >= 0"
      class="pointer-events-none absolute inset-y-0.5 left-0.5 rounded-md bg-text-primary transition-transform duration-(--motion-fluid) ease-(--ease-fluid)"
      :style="{
        width: `calc((100% - 0.25rem) / ${options.length})`,
        transform: `translateX(${indiceSelezionato * 100}%)`,
      }"
      aria-hidden="true"
      data-test="segment-thumb"
    />
    <button
      v-for="(opzione, i) in options"
      :key="opzione.value"
      :ref="(el) => (bottoni[i] = el as HTMLButtonElement | null)"
      type="button"
      role="radio"
      :aria-checked="opzione.value === model ? 'true' : 'false'"
      :aria-label="opzione.ariaLabel"
      :tabindex="i === indiceFocalizzabile ? 0 : -1"
      :disabled="disabled || opzione.disabled"
      :class="[
        'focusable relative z-10 inline-flex cursor-pointer select-none items-center justify-center gap-1.5 rounded-md px-3 font-medium transition-colors duration-(--motion-slow) max-lg:min-h-11',
        SIZES[size],
        opzione.value === model
          ? 'text-background'
          : 'text-text-secondary enabled:hover:bg-border/50 enabled:hover:text-text-primary',
      ]"
      @click="scegli(i)"
      @keydown="onKeydown($event, i)"
    >
      <span
        v-if="opzione.icon"
        class="inline-flex size-[1em] shrink-0 items-center justify-center leading-none"
        aria-hidden="true"
        data-test="segment-icon"
      >
        <i :class="[opzione.icon, 'block leading-none icon-hover-pop']" />
      </span>
      <span v-if="opzione.label" class="leading-none">{{ opzione.label }}</span>
    </button>
  </div>
</template>

<script setup lang="ts" generic="T extends string | number">
// Toggle a segmenti per scegliere una sola opzione (Mappa / Lista, periodo, ordine). Sostituisce SelectButton di
// PrimeVue. Gruppo di radio: una sola opzione ha il Tab (tabindex 0), le frecce spostano scelta e focus (con giro
// completo), Home/End vanno al primo e all'ultimo. Le opzioni disabilitate si saltano (design-system.md, sezione 4).
import type { OpzioneSegmento } from "~/types/ui";

type Dimensione = "sm" | "md";

const props = withDefaults(
  defineProps<{
    options: OpzioneSegmento<T>[];
    // nome del gruppo per gli screen reader (es. "Vista")
    ariaLabel: string;
    size?: Dimensione;
    disabled?: boolean;
    fullWidth?: boolean;
  }>(),
  { size: "md", disabled: false, fullWidth: false },
);

const model = defineModel<T | null>({ default: null });
const bottoni = ref<Array<HTMLButtonElement | null>>([]);

const SIZES: Record<Dimensione, string> = { sm: "h-7 text-xs", md: "h-9 text-sm" };

const abilitata = (i: number) => !props.disabled && !props.options[i]?.disabled;
const indiceSelezionato = computed(() => props.options.findIndex((o) => o.value === model.value));

// Un solo elemento del gruppo è raggiungibile con Tab: quello scelto, o il primo abilitato se non c'è scelta.
const indiceFocalizzabile = computed(() => {
  if (indiceSelezionato.value >= 0 && abilitata(indiceSelezionato.value)) return indiceSelezionato.value;
  return props.options.findIndex((_, i) => abilitata(i));
});

if (import.meta.dev) {
  for (const o of props.options) {
    if (nomeAccessibileMancante({ label: o.label, ariaLabel: o.ariaLabel })) {
      console.warn(`[SegmentedControl] l'opzione "${String(o.value)}" non ha nome accessibile: passa label o ariaLabel.`);
    }
  }
}

function scegli(i: number): void {
  const opzione = props.options[i];
  if (!opzione || !abilitata(i)) return;
  model.value = opzione.value;
}

function vai(i: number): void {
  if (i < 0) return;
  scegli(i);
  bottoni.value[i]?.focus();
}

function onKeydown(evento: KeyboardEvent, i: number): void {
  if (props.disabled) return;
  const destinazione = destinazioneDaTasto(evento.key, i, props.options.length, abilitata);
  if (destinazione === undefined) return;
  evento.preventDefault();
  vai(destinazione);
}
</script>
